<template>
    <div class="iframe-form-content">
      <div class="flex flex-wrap justify-end">
        <vs-input
          class="w-full name-field"
          label="iFrame name"
          v-model="localIFrame.name"
          @blur="saveIFrame(localIFrame)"
        />
      </div>
      <div class="w-full mt-10">
        <label class="vs-input--label">Slug</label>
        <vx-input-group class="mb-base" prependClasses="border border-solid border-grey border-r-0">
          <template slot="prepend">
            <div class="prepend-text">
              <vs-switch v-model="autoAlias" :checked="autoAlias" class="auto-alias-checkbox"/>
            </div>
          </template>

          <vs-input
            v-model="localIFrame.slug" 
            :disabled="autoAlias"
            class="auto-alias-field"
            @blur="saveIFrame(localIFrame)"
          />
        </vx-input-group>
      </div>
      <div class="flex flex-wrap md:mt-10 mt-6">
        <vs-input
          class="w-full duration-field"
          label="Duration"
          v-model="duration"
          readonly
        />
      </div>
      <div class="w-full mt-10 pb-10">
        <label class="vs-input--label">Type</label>
        <v-select
          v-model="localIFrame.type"
          placeholder="Select a type of iframe"
          :options="iframeOptions"
          @input="saveIFrame(localIFrame)"
          :clearable="false"
        />
      </div>
      <div class="flex flex-wrap pb-10 justify-end" v-if="localIFrame.type === 'slides'">
        <vs-input
          type="number"
          class="w-full url-field"
          label="Number of slides"
          min="0"
          v-model="localIFrame.metadata.slides_count"
          @blur="saveIFrame(localIFrame)"
        />
      </div>
      <div class="flex flex-wrap pb-10 justify-end">
        <vs-input
          class="w-full url-field"
          label="URL"
          v-model="localIFrame.url"
          @blur="saveIFrame(localIFrame)"
        />
      </div>
      <div class="flex flex-wrap mt-10">
        <vs-alert color="warning" icon-pack="feather" icon="icon-info" class="w-full" v-if="rememberToSaveFiles">
          Please remember to save the iFrame after add or delete a file.
        </vs-alert>
        <FilesAttachments
          type="iframe"
          :files="getCurrentIFrameFiles"
          :loading="filesLoading"
          :content="getCurrentIFrame"
          @file-deleted="deleteFileFromIFrame"
          @file-added="addFileToIFrame"
          @file-changed="fileChanged"
        />
      </div>
      <div class="flex md:mt-10 mt-6 -mx-2 action-buttons" v-if="embedded">
        <vs-button class="w-full mx-2 save-button" color="primary" icon-pack="feather" @click.stop="saveAndClick">Save</vs-button>
      </div>
    </div>
</template>
<script>
import vSelect from "vue-select"
import slug from 'slug-generator'
import cloneDeep from 'lodash.clonedeep'
import { mapGetters, mapMutations, mapActions } from 'vuex'
import FilesAttachments from '@/components/FilesEngineAttachments'
import { HmsToSeconds, secondsToHms } from '@/utils/utils.js'
import { warningUnsaved } from "@/utils/mixins"

export default {
  mixins: [warningUnsaved],
  props: {
    embedded: Boolean,
    onClick: {
      type: Function,
      required: true
    },
  },
  components: {
    "v-select": vSelect,
    FilesAttachments
  },
  data () {
    return {
      autoAlias: true,
      localIFrame: {},
      iframeOptions: ['slides'],
      explanationOptions: [],
      solutionOptions: [],
      rememberToSaveFiles: false,
      filesLoading: false,
      duration: '00:00:00',
      durationInSeconds: '0',
    }
  },
  watch: {
    'localIFrame.name': function (newValue) {
      if (this.autoAlias) {
        this.localIFrame.slug = slug(newValue)
      }
    },
    'autoAlias': function (newValue) {
      if (newValue) {
        this.localIFrame.slug = slug(this.localIFrame.name)
      }
    },
    'getCurrentIFrame': function (newValue) {
      this.localIFrame = cloneDeep(newValue)
      this.durationInSeconds = this.localIFrame.duration_in_seconds
      this.duration = secondsToHms(this.durationInSeconds)
    },
    'duration': function (newValue) {
      this.durationInSeconds = HmsToSeconds(newValue)
      this.transformAndSaveIFrame()
    },
    'localIFrame.files': async function () {
      if( JSON.stringify(this.localIFrame.files) === JSON.stringify(this.getCurrentIFrame.files)) {
        this.rememberToSaveFiles = false
      }
      if (this.localIFrame.files && this.localIFrame.files.length) {
        this.filesLoading = true
        await this.getAllIFrameFiles(this.localIFrame.files)
        this.filesLoading = false
      }
      if (this.localIFrame.files && !this.localIFrame.files.length) {
        this.resetFilesInIFrame()
      }
    },
    'rememberToSaveFiles': function (newValue) {
      this.$emit('prevent-close', newValue)
    },
  },
  computed: {
    ...mapGetters('iframes', ['getCurrentIFrame', 'getCurrentIFrameFiles']),
    isEditing () {
      return !!this.getCurrentIFrame.id
    },
  },
  methods: {
    ...mapMutations('iframes', {
      saveIFrame: 'STORE_CURRENT_IFRAME',
      changeFileInIFrame: 'CHANGE_FILE_IN_IFRAME',
      resetFilesInIFrame: 'RESET_FILES_IN_IFRAME'
    }),
    ...mapActions('iframes', ['getAllIFrameFiles']),
    saveAndClick() {
      this.saveIFrame(this.localIFrame)
      this.onClick()
    },
    saveIFrameIfNew() {
      if (!this.isEditing) {
        this.saveIFrame(this.localIFrame)
      }
    },
    deleteFileFromIFrame(file) {
      const index = this.localIFrame.files.indexOf(file.id)
      if (index > -1) {
        this.localIFrame.files.splice(index, 1);
      }
      this.rememberToSaveFiles = true
    },
    addFileToIFrame(file) {
      this.localIFrame.files = [...this.localIFrame.files, file.id]
      this.rememberToSaveFiles = true
    },
    fileChanged(payload) {
      this.changeFileInIFrame(payload)
    },
    transformAndSaveIFrame() {
      this.localIFrame.duration_in_seconds = this.durationInSeconds
      this.saveIFrame(this.localIFrame)
    },
  },
  async mounted () {
    this.localIFrame = cloneDeep(this.getCurrentIFrame)
    this.durationInSeconds = this.localIFrame.duration_in_seconds
    this.duration = secondsToHms(this.durationInSeconds)
  }
}
</script>